import styled from '@emotion/styled';

import { Link, useNavigate } from 'react-router-dom';

import { Grid, Card, CardHeader, CardContent, IconButton, Button } from '@mui/material';

import { CardBlockContainer, CardBlock, CardBlockHeader, CardSpacer } from '../../../utils/utilities/CardComponents';

import MultiStatusChip from '../../../utils/utilities/MultiStatusChip';

import SimpleChip from '../../../utils/utilities/SimpleChip';

import { relativeTime, formatPhoneNumber } from '../../../utils/util';

import handleErrors from '../../../utils/errorHandler';

import { BasicFlex } from '../../../utils/Common';

import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import AccountBoxOutlinedIcon from '@mui/icons-material/AccountBoxOutlined';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import PhoneAndroidRoundedIcon from '@mui/icons-material/PhoneAndroidRounded';
import EditOutlined from '@mui/icons-material/EditOutlined';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';

import NoTimeline from '../../../utils/utilities/Timeline/NoTimeline';

import ShareCard from '../../../utils/utilities/Buttons/ShareCard';

import TimelineWithComments from '../../../utils/utilities/Timeline/TimelineWithComments';


// use pageData context
import { usePageData } from '../../../contexts/PageData';

import BillDisplay from '../Components/BillDisplay';

import Dialog from '../../../utils/utilities/Dialog';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteBill } from '../apiService';
import useSnackbarStore from '../../../store/snackbar';

const DeleteMessage = styled.div`
  padding: 0 1.6rem;
  line-height: 1.6;
`


const DetailsTab = () => {
  const { data = {} } = usePageData();

  let vendor = data?.vendor || {};

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  const { mutate: deleteBillMutation } = useMutation({
    mutationFn: deleteBill,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      navigate('/bills');
    },
    onError: (error) => showSnackbar(handleErrors(error), 'error'),
  });

  const handleDelete = () => deleteBillMutation(data.bill_id);

  const vendorBlock = [
    {header: "Vendor", icon: <AccountBoxOutlinedIcon fontSize='small' />, info: vendor.full_name},
    {header: "Email", icon: <PhoneAndroidRoundedIcon fontSize='small' />, info: vendor.email},
  ]

  const basicBlock = [
    {header: "Bill ID", icon: <ConfirmationNumberRoundedIcon fontSize='small' color="basic" />, info: `#${data.bill_num}`},
    {header: "Date", info: data.date, dataType: "date"},
  ]

  const billBlock = [
    {header: "Bill Total",  info: data.total, dataType: "currency"},
    {header: "Paid Status", info: <MultiStatusChip value={data.paid_status} values={[{value: "unpaid", label: "Unpaid", status: "error"}, {value: "partially_paid", label: "Partially Paid", status: "warning"}, {value: "paid", label: "Paid", status: "success"}]} />},
    {header: "Paid Amount", info: data.total_paid, dataType: "currency"},
    {header: "Balance", info: data.balance, dataType: "currency"},
  ]

  return (
    <Grid container spacing={3}>
      <Grid size={{xs: 12, md: 8}}>
        <Grid container spacing={3}>
          <Grid size={{xs: 12}}>
            <Card variant='outlined'>
              <CardHeader title="Bill Summary" action={<Link to={`/bills/edit/${data.bill_id}`}><IconButton color="secondary"><EditOutlined /></IconButton></Link>} />
              <CardContent>
                <CardBlockContainer>
                  {vendorBlock.map((block) => (
                    <CardBlock key={block.header} {...block} />
                  ))}

                  <CardSpacer />
                  <CardBlockHeader>General Details</CardBlockHeader>
                  {basicBlock.map((block) => (
                    <CardBlock key={block.header} {...block} />
                  ))}

                  <CardSpacer />
                  <CardBlockHeader>Bill Details</CardBlockHeader>
                  {billBlock.map((block) => (
                    <CardBlock key={block.header} {...block} />
                  ))}
                  <CardSpacer />
                </CardBlockContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{xs: 12}}>
            <BillDisplay bill={data} />
          </Grid>
        </Grid>
      </Grid>

      <Grid size={{xs: 12, md: 4}}>
        <Dialog title="Confirm Cancel Bill?" size="sm" toggle={<Button size="large" variant="contained" startIcon={<DeleteForeverRoundedIcon />} fullWidth sx={{backgroundColor: "error.lighter", mb: 2}}>Delete Bill</Button>} saveText="Delete"  saveFunc={() => handleDelete()}>
        <DeleteMessage>
          Deleting this bill will permanently remove it from the system. <br /> The original Payments attached to the bill will NOT be deleted, only the applied payments will be deleted. This might leave the vendor with a negative balance if there are any payments applied to the bill.<br /> Are you sure you want to continue?
        </DeleteMessage>
        </Dialog>
        <ShareCard url={`/bills/generate/${data.bill_id}`} defaultEmail={vendor.email} title="Bill Actions" />
        <Card variant='outlined' sx={{mt: 2}}>
          <CardHeader title={<BasicFlex gap="1rem">Bill History <HistoryRoundedIcon /></BasicFlex>} />
          <CardContent>
            <TimelineWithComments resource="bill" resource_id={data.bill_id} expand={true} />
          </CardContent>
        </Card>
      </Grid>
 
    </Grid>
  );

};

export default DetailsTab;